// Public video feed - vertical scroll with side comments drawer
import { useEffect, useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { getApprovedVideos, getVideoUrl, getCommentsForVideo, addComment, upvoteVideo, getUpvoteStatuses } from '@/lib/supabase'
import { apiClient } from '@/lib/api'
import type { Video, Comment } from '@/types'

const PAGE_SIZE = 10

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`
  return date.toLocaleDateString()
}

async function hashToken(token: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(token)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
}

interface VideoCardProps {
  video: Video
  sessionTokenHash: string | null
  isUpvoted: boolean
  onUpvote: (videoId: string) => void
  onOpenComments: (video: Video) => void
}

function VideoCard({ video, sessionTokenHash, isUpvoted, onUpvote, onOpenComments }: VideoCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [localUpvotes, setLocalUpvotes] = useState(video.upvotes)
  const [localUpvoted, setLocalUpvoted] = useState(isUpvoted)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showCopied, setShowCopied] = useState(false)
  const [isMuted, setIsMuted] = useState(true)

  const videoUrl = getVideoUrl(video.r2_key)

  useEffect(() => {
    setLocalUpvoted(isUpvoted)
  }, [isUpvoted])

  const handleUpvote = async () => {
    if (!sessionTokenHash || localUpvoted) return
    setLocalUpvoted(true)
    setLocalUpvotes(prev => prev + 1)
    onUpvote(video.id)
  }

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/feed?v=${video.id}`

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Philly Watch Video',
          text: video.neighborhood ? `Activity documented in ${video.neighborhood}` : 'Community documentation video',
          url: shareUrl,
        })
      } catch (err) {
        // User cancelled or error
        if ((err as Error).name !== 'AbortError') {
          copyToClipboard(shareUrl)
        }
      }
    } else {
      copyToClipboard(shareUrl)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setShowCopied(true)
    setTimeout(() => setShowCopied(false), 2000)
  }

  const handleDownload = () => {
    if (!videoUrl) return
    // Open video in new tab - user can right-click save or use browser download
    window.open(videoUrl, '_blank')
  }

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(videoRef.current.muted)
    }
  }

  const toggleFullscreen = () => {
    if (!videoRef.current) return

    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen().catch(() => {})
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Play/pause based on visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            videoRef.current?.play().catch(() => {})
          } else {
            videoRef.current?.pause()
          }
        })
      },
      { threshold: 0.5 }
    )

    if (videoRef.current) {
      observer.observe(videoRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div className="flex justify-center py-4 snap-start">
      <div className="relative w-full max-w-[400px] mx-2 sm:mx-4">
        {/* Video container with rounded corners */}
        <div className="relative bg-gray-900 rounded-xl overflow-hidden aspect-[9/16] flex items-center justify-center">
          <video
            ref={videoRef}
            src={videoUrl || ''}
            className="max-w-full max-h-full object-contain"
            loop
            playsInline
            muted
            onClick={(e) => {
              const vid = e.currentTarget
              vid.paused ? vid.play() : vid.pause()
            }}
          />

          {/* Mute/Unmute button */}
          <button
            onClick={toggleMute}
            className="absolute top-3 left-3 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMuted ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              )}
            </svg>
          </button>

          {/* Fullscreen button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isFullscreen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
              )}
            </svg>
          </button>

          {/* Action buttons - inside video, right side */}
          <div className="absolute right-2 bottom-20 flex flex-col items-center gap-3">
            {/* Boost visibility */}
            <button
              onClick={handleUpvote}
              disabled={!sessionTokenHash || localUpvoted}
              className="flex flex-col items-center"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                localUpvoted ? 'bg-blue-500' : 'bg-black/50 hover:bg-black/70'
              }`}>
                <svg className="w-5 h-5 text-white" fill={localUpvoted ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </div>
              <span className="text-white text-xs mt-0.5 drop-shadow">{localUpvotes}</span>
            </button>

            {/* Comments */}
            <button onClick={() => onOpenComments(video)} className="flex flex-col items-center">
              <div className="w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <span className="text-white text-xs mt-0.5 drop-shadow">{video.comment_count}</span>
            </button>

            {/* Share */}
            <button onClick={handleShare} className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                showCopied ? 'bg-green-600' : 'bg-black/50 hover:bg-black/70'
              }`}>
                {showCopied ? (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                )}
              </div>
              <span className="text-white text-xs mt-0.5 drop-shadow">{showCopied ? 'Copied!' : 'Share'}</span>
            </button>

            {/* Download */}
            <button onClick={handleDownload} className="flex flex-col items-center">
              <div className="w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <span className="text-white text-xs mt-0.5 drop-shadow">Save</span>
            </button>

            {/* Location */}
            {video.gps_lat && video.gps_lng && (
              <a
                href={`https://maps.google.com/?q=${video.gps_lat},${video.gps_lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center"
              >
                <div className="w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="text-white text-xs mt-0.5 drop-shadow">Map</span>
              </a>
            )}
          </div>

          {/* Bottom info overlay */}
          <div className="absolute left-3 bottom-3 right-16">
            <div className="flex items-center gap-2 text-white text-sm">
              {video.neighborhood && (
                <span className="font-medium bg-black/40 px-2 py-0.5 rounded">
                  {video.neighborhood}
                </span>
              )}
              <span className="text-white/70 text-xs drop-shadow">{formatTimeAgo(video.created_at)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CommentsDrawerProps {
  video: Video | null
  isOpen: boolean
  onClose: () => void
  sessionTokenHash: string | null
}

function CommentsDrawer({ video, isOpen, onClose, sessionTokenHash }: CommentsDrawerProps) {
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Load comments when video changes
  useEffect(() => {
    if (!video || !isOpen) {
      setComments([])
      return
    }

    async function loadComments() {
      setLoading(true)
      try {
        const data = await getCommentsForVideo(video!.id)
        setComments(data)
      } catch (err) {
        console.error('Failed to load comments:', err)
      } finally {
        setLoading(false)
      }
    }
    loadComments()
  }, [video, isOpen])

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim() || !sessionTokenHash || !video || submitting) return

    setSubmitting(true)
    try {
      const comment = await addComment(video.id, newComment, sessionTokenHash)
      setComments(prev => [...prev, comment])
      setNewComment('')
    } catch (err) {
      console.error('Failed to post comment:', err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-gray-900 z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold text-lg">
            {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ height: 'calc(100% - 140px)' }}>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-500">No comments yet</p>
              <p className="text-gray-600 text-sm mt-1">Be the first to comment</p>
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="bg-gray-800 rounded-lg p-3">
                <p className="text-white text-sm leading-relaxed">{comment.body}</p>
                <span className="text-xs text-gray-500 mt-2 block">{formatTimeAgo(comment.created_at)}</span>
              </div>
            ))
          )}
        </div>

        {/* Comment input */}
        <form onSubmit={handleSubmit} className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800 bg-gray-900">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={sessionTokenHash ? "Add a comment..." : "Loading..."}
              disabled={!sessionTokenHash || submitting}
              maxLength={500}
              className="flex-1 bg-gray-800 text-white px-4 py-3 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!newComment.trim() || !sessionTokenHash || submitting}
              className="px-5 py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-full text-sm font-medium transition-colors"
            >
              Post
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default function FeedPage() {
  const [videos, setVideos] = useState<Video[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionTokenHash, setSessionTokenHash] = useState<string | null>(null)
  const [upvotedVideos, setUpvotedVideos] = useState<Set<string>>(new Set())
  const [commentsVideo, setCommentsVideo] = useState<Video | null>(null)
  const loaderRef = useRef<HTMLDivElement>(null)

  // Create session for interactions
  useEffect(() => {
    async function initSession() {
      try {
        const token = await apiClient.createSession()
        const hash = await hashToken(token)
        setSessionTokenHash(hash)
      } catch (err) {
        console.error('Failed to create session:', err)
      }
    }
    initSession()
  }, [])

  // Initial load
  useEffect(() => {
    async function fetchVideos() {
      try {
        const data = await getApprovedVideos(PAGE_SIZE, 0)
        setVideos(data)
        setHasMore(data.length === PAGE_SIZE)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load videos')
      } finally {
        setLoading(false)
      }
    }
    fetchVideos()
  }, [])

  // Load upvote statuses when videos and session are ready
  useEffect(() => {
    async function loadUpvoteStatuses() {
      if (!sessionTokenHash || videos.length === 0) return
      const statuses = await getUpvoteStatuses(videos.map(v => v.id), sessionTokenHash)
      setUpvotedVideos(statuses)
    }
    loadUpvoteStatuses()
  }, [sessionTokenHash, videos])

  // Infinite scroll
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const data = await getApprovedVideos(PAGE_SIZE, videos.length)
      setVideos(prev => [...prev, ...data])
      setHasMore(data.length === PAGE_SIZE)

      // Load upvote statuses for new videos
      if (sessionTokenHash && data.length > 0) {
        const newStatuses = await getUpvoteStatuses(data.map(v => v.id), sessionTokenHash)
        setUpvotedVideos(prev => new Set([...prev, ...newStatuses]))
      }
    } catch (err) {
      console.error('Failed to load more:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [videos.length, loadingMore, hasMore, sessionTokenHash])

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => observer.disconnect()
  }, [loadMore])

  const handleUpvote = async (videoId: string) => {
    if (!sessionTokenHash) return
    try {
      await upvoteVideo(videoId, sessionTokenHash)
      setUpvotedVideos(prev => new Set([...prev, videoId]))
    } catch (err) {
      console.error('Failed to upvote:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-black p-6">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-white"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (videos.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center bg-black p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No videos yet</h3>
          <p className="text-gray-400 mb-4">Be the first to document activity</p>
          <Link
            to="/"
            className="inline-block px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Start Recording
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 bg-black overflow-y-scroll snap-y snap-mandatory scrollbar-hide pb-20">
        {videos.map(video => (
          <VideoCard
            key={video.id}
            video={video}
            sessionTokenHash={sessionTokenHash}
            isUpvoted={upvotedVideos.has(video.id)}
            onUpvote={handleUpvote}
            onOpenComments={setCommentsVideo}
          />
        ))}

        {/* Infinite scroll loader */}
        <div ref={loaderRef} className="h-20 flex items-center justify-center snap-start">
          {loadingMore && (
            <div className="w-6 h-6 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
          )}
          {!hasMore && videos.length > 0 && (
            <p className="text-gray-500 text-sm">You've seen everything</p>
          )}
        </div>
      </div>

      {/* Comments drawer */}
      <CommentsDrawer
        video={commentsVideo}
        isOpen={!!commentsVideo}
        onClose={() => setCommentsVideo(null)}
        sessionTokenHash={sessionTokenHash}
      />
    </>
  )
}
